import React from "react";
import ReactMarkdown from "react-markdown";
import { extractYouTubeId } from "../VideoComp/YouTubeVideo";


// types/article.ts
export type Article = {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  author?: { name: string };
  publishedAt?: string; // ISO string
  tags?: string[];
  heroImage?: { url: string; alt?: string };
  blocks: ArticleBlock[];
};

export type ArticleBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; level: 1 | 2 | 3 | 4; text: string }
  | { type: "list"; style: "bullet" | "ordered"; items: string[] }
  | { type: "quote"; text: string; cite?: string }
  | { type: "code"; language?: string; code: string }
  | { type: "image"; url: string; alt?: string; caption?: string }
  // `url` can be any YouTube link (watch/youtu.be/embed/shorts) or a bare video ID.
  | { type: "video"; url: string; title?: string; caption?: string };


async function fetchArticle(slug: string): Promise<Article> {
  const res = await fetch(`/api/articles/${encodeURIComponent(slug)}`, {
    headers: { "Accept": "application/json" },
  });

  if (!res.ok) throw new Error(`Failed to load article (${res.status})`);
  return (await res.json()) as Article;
}

export function ArticleElement({ slug, articles }: { slug: string, articles: Article[] }) {
  const [data, setData] = React.useState<Article | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const article = articles.find(article => article.slug === slug) as Article;//await fetchArticle(slug);
        if (!cancelled) setData(article);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (err) return <div style={{ padding: 24 }}>Error: {err}</div>;
  if (!data) return <div style={{ padding: 24 }}>Not found</div>;

  return (
    <main style={styles.page}>
      <article style={styles.article}>
        <ArticleHeader article={data} />
        <ArticleBody blocks={data.blocks} />
      </article>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { display: "flex", justifyContent: "center", padding: "32px 16px" },
  article: { width: "100%", maxWidth: 820, display: "flex", flexDirection: "column", gap: 20 },
};

export function ArticleHeader({ article }: { article: Article }) {
  const date = article.publishedAt ? new Date(article.publishedAt) : null;

  return (
    <header className="article-container">   {/*style={{ display: "flex", flexDirection: "column", gap: 12 }}>*/}
      {article.heroImage?.url ? (
        <img
          src={article.heroImage.url}
          alt={article.heroImage.alt ?? article.title}
          
        />
      ) : null} {/*style={{ width: "100%", borderRadius: 12, objectFit: "cover" }}*/}

      <h1 >{article.title}</h1> {/*style={{ fontSize: 34, lineHeight: 1.15, margin: 0 }}*/}

      <div > {/*style={{ display: "flex", gap: 12, flexWrap: "wrap", opacity: 0.8 }}*/}
        {/* {article.author?.name ? <span>By: {article.author.name}</span> : null} */}
        {date ? <span> {date.toLocaleDateString()}</span> : null}
        {/* {article.tags?.length ? <span> <br/> Tags: {article.tags.join(", ")}</span> : null} */}
      </div>
      {article.excerpt ? <div><h2 >Excerpt</h2><p style={{ fontSize: 18, margin: 0 }}><ReactMarkdown>{article.excerpt}</ReactMarkdown></p></div> : null}
      <br/>
      <hr /> {/*style={{ width: "100%", opacity: 0.2 }} */}
    </header>
  );
}

export function ArticleBody({ blocks }: { blocks: ArticleBlock[] }) {
  return (
    <section className='description-container'>  {/*style={{ display: "flex", flexDirection: "column", gap: 14 }}*/}
      <h2 >Article</h2>
      {blocks.map((b, idx) => (
        <Block key={idx} block={b} />
      ))}
    </section>
  );
}

function Block({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case "paragraph":
      return <div><p style={{ fontSize: 16, lineHeight: 1.7, margin: 0 }}>{block.text}</p><br/></div>;

    case "heading": {
      const Tag = (`h${block.level}` as unknown) as keyof JSX.IntrinsicElements;
      const size = block.level === 2 ? 24 : block.level === 3 ? 20 : 28;
      return <Tag >{block.text}</Tag>; {/*style={{ margin: "12px 0 0", fontSize: size }}*/}
    }

    case "list":
      return block.style === "ordered" ? (
        <ol > {/*style={{ paddingLeft: 22, margin: 0, lineHeight: 1.7 }}*/}
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      ) : (
        <ul style={{ paddingLeft: 22, margin: 0, lineHeight: 1.7 }}>
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );

    case "quote":
      return (
        <blockquote > {/*style={{ margin: 0, padding: "10px 14px", borderLeft: "4px solid #999", opacity: 0.9 }}*/}
          <div >{block.text}</div> {/*style={{ fontStyle: "italic" }}*/}
          {block.cite ? <div style={{ marginTop: 8, fontSize: 14 }}>— {block.cite}</div> : null}
        </blockquote>
      );

    case "code":
      return (
        <pre style={{ margin: 0, padding: 14, borderRadius: 10, overflowX: "auto", background: "rgba(0,0,0,0.06)" }}>
          <code>{block.code}</code>
        </pre>
      );

    case "image":
      return (
        <figure> {/*style={{ margin: 0, display: "flex", flexDirection: "column", gap: 8 }}*/}
          <img src={block.url} alt={block.alt ?? ""} style={{ width: "100%", borderRadius: 12 }} />
          {block.caption ? <figcaption style={{ fontSize: 14, opacity: 0.75 }}>{block.caption}</figcaption> : null}
        </figure>
      );

    case "video":
      return (
        <figure style={{ margin: "0 0 20px" }}>
          <iframe
            src={`https://www.youtube.com/embed/${extractYouTubeId(block.url)}?rel=0`}
            title={block.title ?? "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ width: "100%", aspectRatio: "16/9", border: 0, borderRadius: 12, display: "block" }}
          />
          {block.caption ? <figcaption style={{ fontSize: 14, opacity: 0.75 }}>{block.caption}</figcaption> : null}
        </figure>
      );

    default:
      // Exhaustiveness check (if you add new block types, TS will help you update renderer)
      return null;
  }
}

